import { useFormContext } from 'react-hook-form';
import { motion } from 'framer-motion';
import { Building2, CreditCard, AlertTriangle } from 'lucide-react';
import { StripeNodeFormValues } from '../../../types/stripe';

interface Step2AccountBehaviorProps {
	config: StripeNodeFormValues;
	onChange: (config: Partial<StripeNodeFormValues>) => void;
}

export function Step2AccountBehavior({ config, onChange }: Step2AccountBehaviorProps) {
	const { register, watch } = useFormContext<StripeNodeFormValues>();
	const watchedStripeAccountMode = watch('stripeAccountMode');

	const handleStripeAccountModeChange = (mode: 'platform' | 'connect') => {
		onChange({ stripeAccountMode: mode });
	};

	return (
		<div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 space-y-6">
			<div>
				<h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
					Account Settings
				</h3>
				<p className="text-sm text-gray-600 dark:text-gray-400">
					Configure how this node interacts with Stripe accounts
				</p>
			</div>

			{/* Stripe Account Mode */}
			<div className="space-y-4">
				<h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
					Stripe Account Mode
				</h4>
				<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
					<label className="block">
						<input
							type="radio"
							value="platform"
							{...register('stripeAccountMode')}
							className="sr-only"
							onChange={(e) => handleStripeAccountModeChange(e.target.value as 'platform' | 'connect')}
						/>
						<div
							className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
								watchedStripeAccountMode === 'platform'
									? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
									: 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
							}`}
						>
							<div className="flex items-start space-x-3">
								<div
									className={`p-2 rounded-lg ${
										watchedStripeAccountMode === 'platform'
											? 'bg-purple-100 dark:bg-purple-800 text-purple-600 dark:text-purple-400'
											: 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
									}`}
								>
									<Building2 className="w-5 h-5" />
								</div>
								<div className="flex-1">
									<h5 className="text-sm font-medium text-gray-900 dark:text-white">
										Platform Account
									</h5>
									<p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
										Use your main Stripe account for all operations
									</p>
								</div>
							</div>
						</div>
					</label>

					<label className="block">
						<input
							type="radio"
							value="connect"
							{...register('stripeAccountMode')}
							className="sr-only"
							onChange={(e) => handleStripeAccountModeChange(e.target.value as 'platform' | 'connect')}
						/>
						<div
							className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
								watchedStripeAccountMode === 'connect'
									? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
									: 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
							}`}
						>
							<div className="flex items-start space-x-3">
								<div
									className={`p-2 rounded-lg ${
										watchedStripeAccountMode === 'connect'
											? 'bg-purple-100 dark:bg-purple-800 text-purple-600 dark:text-purple-400'
											: 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
									}`}
								>
									<CreditCard className="w-5 h-5" />
								</div>
								<div className="flex-1">
									<h5 className="text-sm font-medium text-gray-900 dark:text-white">
										Connect Account
									</h5>
									<p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
										Use a connected Stripe account (marketplace/platform)
									</p>
								</div>
							</div>
						</div>
					</label>
				</div>
			</div>

			{/* Connected Account ID (only show if connect mode) */}
			{watchedStripeAccountMode === 'connect' && (
				<motion.div
					initial={{ opacity: 0, y: 10 }}
					animate={{ opacity: 1, y: 0 }}
					className="space-y-3"
				>
					<label className="block">
						<span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
							Connected Account ID
						</span>
						<input
							type="text"
							{...register('connectedAccountIdPath')}
							placeholder="$.stripe.connectedAccountId or static value"
							className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 dark:focus:ring-purple-400 focus:border-purple-500 dark:focus:border-purple-400 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
						/>
						<p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
							The Stripe account ID to use for this operation
						</p>
					</label>
				</motion.div>
			)}

			{/* Webhook Wait Option */}
			<div className="space-y-3">
				<h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
					Behavior Settings
				</h4>
				<label className="flex items-start space-x-3">
					<input
						type="checkbox"
						{...register('waitForWebhook')}
						className="mt-1 h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
					/>
					<div className="flex-1">
						<span className="text-sm font-medium text-gray-900 dark:text-white">
							Wait for Webhook
						</span>
						<p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
							Pause execution until a webhook event is received (useful for async operations)
						</p>
					</div>
				</label>
			</div>

			{/* Warning for Connect Mode */}
			{watchedStripeAccountMode === 'connect' && (
				<motion.div
					initial={{ opacity: 0, y: 10 }}
					animate={{ opacity: 1, y: 0 }}
					className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg"
				>
					<div className="flex items-start space-x-3">
						<AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mt-0.5" />
						<div>
							<h5 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
								Connect Mode Active
							</h5>
							<p className="text-xs text-yellow-700 dark:text-yellow-300 mt-1">
								Make sure to provide a valid connected account ID. Operations will fail if the account ID is invalid or the account doesn't have the required permissions.
							</p>
						</div>
					</div>
				</motion.div>
			)}
		</div>
	);
}
